import { OrderChargeType } from "Infrastructure/Api/Api";
import {
	chargesAtom,
	feesAtom,
	paymentDetailAtom,
} from "Order/Atom/paymentAtom";
import { useOrderChargeListQuery } from "Order/Modules/Payment/Api/Query/useOrderChargeListQuery";
import { useOrderFeeQuery } from "Order/Modules/Payment/Api/Query/useOrderFeeQuery";
import { useOrderPaymentDetailQuery } from "Order/Modules/Payment/Api/Query/useOrderPaymentDetailQuery";
import { useAtom } from "jotai";
import { useEffect, useMemo } from "react";

/**
 * Custom hook to handle payment data fetching and state management
 */
export function usePayment(orderId: number) {
	const [paymentDetail, setPaymentDetail] = useAtom(paymentDetailAtom);
	const [charges, setCharges] = useAtom(chargesAtom);
	const [fees, setFees] = useAtom(feesAtom);

	const chargesQuery = useOrderChargeListQuery(orderId);
	const paymentDetailQuery = useOrderPaymentDetailQuery(orderId);
	const feesQuery = useOrderFeeQuery(orderId);

	// Payment detail
	useEffect(() => {
		if (paymentDetailQuery.isSuccess && paymentDetailQuery.data) {
			setPaymentDetail(paymentDetailQuery.data);
		}
	}, [paymentDetailQuery.isSuccess, paymentDetailQuery.data, setPaymentDetail]);

	// Charges
	useEffect(() => {
		if (chargesQuery.isSuccess && chargesQuery.data) {
			setCharges(chargesQuery.data);
		}
	}, [chargesQuery.isSuccess, chargesQuery.data, setCharges]);

	// Fees
	useEffect(() => {
		if (feesQuery.isSuccess && feesQuery.data) {
			setFees(feesQuery.data);
		}
	}, [feesQuery.isSuccess, feesQuery.data, setFees]);

	const isLoading =
		chargesQuery.isLoading ||
		paymentDetailQuery.isLoading ||
		feesQuery.isLoading;

	const expressPayoutSent = useMemo(
		() =>
			chargesQuery.data?.some(
				(charge) =>
					charge.orderId === orderId &&
					charge.type === OrderChargeType.express_payment,
			) ?? false,
		[chargesQuery.data, orderId],
	);

	return {
		isLoading,
		expressPayoutSent,
		paymentDetail,
		charges,
		fees,
	};
}
